import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  Card,
  CardContent,
  CardActionArea,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EventIcon from '@mui/icons-material/Event';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReplayIcon from '@mui/icons-material/Replay';
import EmptyState from '../../../components/Common/EmptyState';
import { useAuth } from '../../../contexts/AuthContext';
import { useUserOrganizations } from '../hooks/useOrganization';
import { useSurveys } from '../hooks/useSurveys';
import { createSurvey } from '../../../services/surveyService';
import { getDraftResponse } from '../../../services/responseService';
import CreateSurveyDialog from '../components/CreateSurveyDialog';
import { useOrganization } from '../hooks/useOrganization';
import { STANDARD_QUESTION_COUNT } from '../utils/constants';

const STATUS_COLORS = {
  draft: 'default',
  active: 'success',
  closed: 'error',
};

function formatDate(timestamp) {
  if (!timestamp) return '';
  // Handle Firestore Timestamp objects
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function isOverdue(dueDate) {
  if (!dueDate) return false;
  const due = new Date(dueDate);
  due.setHours(23, 59, 59, 999); // End of day
  return due < new Date();
}

function formatDueDate(dueDate) {
  if (!dueDate) return '';
  const date = new Date(dueDate);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function Surveys() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { organizations, loading: orgsLoading } = useUserOrganizations(user?.uid);

  // Use first org for now (MVP)
  const orgId = organizations?.[0]?.id || null;
  const { organization } = useOrganization(orgId);
  const { surveys, loading: surveysLoading } = useSurveys(orgId);

  const [statusFilter, setStatusFilter] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [draftSurveyIds, setDraftSurveyIds] = useState(new Set());

  // Check for draft responses (incomplete surveys) for the current user
  React.useEffect(() => {
    if (!user?.uid || !surveys || surveys.length === 0) return;
    const activeSurveys = surveys.filter((s) => s.status === 'active');
    if (activeSurveys.length === 0) return;

    Promise.all(
      activeSurveys.map((s) =>
        getDraftResponse(s.id, user.uid)
          .then((draft) => (draft ? s.id : null))
          .catch(() => null)
      )
    ).then((results) => {
      const ids = new Set(results.filter(Boolean));
      setDraftSurveyIds(ids);
    });
  }, [user?.uid, surveys]);

  const filteredSurveys = useMemo(() => {
    if (statusFilter === 'all') return surveys;
    return surveys.filter((s) => s.status === statusFilter);
  }, [surveys, statusFilter]);

  const handleCreateSurvey = async (surveyData) => {
    await createSurvey({
      ...surveyData,
      organizationId: orgId,
      createdBy: user.uid,
    });
    setSnackbar({ open: true, message: 'Survey created successfully!', severity: 'success' });
  };

  const handleSurveyClick = (surveyId) => {
    // If the user has an incomplete draft, go directly to take/resume the survey
    if (draftSurveyIds.has(surveyId)) {
      navigate(`/drew/surveys/${surveyId}/take`);
    } else {
      navigate(`/drew/surveys/${surveyId}`);
    }
  };

  const loading = orgsLoading || surveysLoading;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!orgId) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Surveys
        </Typography>
        <Alert severity="info" sx={{ mt: 2 }}>
          You need to create or join an organization first. Go to{' '}
          <strong>Settings → Organization</strong> to set up your organization.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Surveys</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create Survey
        </Button>
      </Box>

      {/* Status filter */}
      <ToggleButtonGroup
        value={statusFilter}
        exclusive
        onChange={(e, val) => val && setStatusFilter(val)}
        size="small"
        sx={{ mb: 3 }}
      >
        <ToggleButton value="all">
          All ({surveys.length})
        </ToggleButton>
        <ToggleButton value="active">
          Active ({surveys.filter((s) => s.status === 'active').length})
        </ToggleButton>
        <ToggleButton value="draft">
          Draft ({surveys.filter((s) => s.status === 'draft').length})
        </ToggleButton>
        <ToggleButton value="closed">
          Closed ({surveys.filter((s) => s.status === 'closed').length})
        </ToggleButton>
      </ToggleButtonGroup>

      {/* Survey list */}
      {filteredSurveys.length === 0 ? (
        <EmptyState
          icon={AssignmentIcon}
          title="No surveys yet"
          description={
            statusFilter === 'all'
              ? 'Create your first survey to get started.'
              : `No ${statusFilter} surveys found.`
          }
          actionLabel={statusFilter === 'all' ? 'Create Survey' : undefined}
          onAction={statusFilter === 'all' ? () => setCreateDialogOpen(true) : undefined}
        />
      ) : (
        <Stack spacing={2} data-tour="survey-list">
          {filteredSurveys.map((survey) => {
            const overdue = survey.status === 'active' && isOverdue(survey.dueDate);
            const hasDraft = draftSurveyIds.has(survey.id);
            return (
              <Card
                key={survey.id}
                variant="outlined"
                sx={overdue ? {
                  borderColor: 'warning.main',
                  borderWidth: 2,
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,167,38,0.08)' : 'rgba(255,167,38,0.04)',
                } : hasDraft ? {
                  borderColor: 'info.main',
                  borderWidth: 2,
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(33,150,243,0.08)' : 'rgba(33,150,243,0.04)',
                } : {}}
              >
                <CardActionArea onClick={() => handleSurveyClick(survey.id)}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
                          <Typography variant="h6" component="div">
                            {survey.title}
                          </Typography>
                          <Chip
                            label={survey.status}
                            color={STATUS_COLORS[survey.status] || 'default'}
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                          />
                          {hasDraft && (
                            <Chip
                              icon={<ReplayIcon />}
                              label="In Progress"
                              color="info"
                              size="small"
                              variant="outlined"
                            />
                          )}
                          {overdue && (
                            <Chip
                              icon={<WarningAmberIcon />}
                              label="Overdue"
                              color="warning"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                        {survey.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {survey.description}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={2} sx={{ mt: 1 }} flexWrap="wrap">
                          <Typography variant="caption" color="text.secondary">
                            Created: {formatDate(survey.createdAt)}
                          </Typography>
                          {survey.dueDate && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                color: overdue ? 'warning.main' : 'text.secondary',
                                fontWeight: overdue ? 600 : 400,
                              }}
                            >
                              <EventIcon sx={{ fontSize: 14 }} />
                              Due: {formatDueDate(survey.dueDate)}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            Responses: {survey.responseCount || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Questions: {survey.questionSet === 'standard' ? `Standard (${STANDARD_QUESTION_COUNT})` : 'Custom'}
                          </Typography>
                        </Stack>
                        {hasDraft && survey.status === 'active' && (
                          <Chip
                            icon={<PlayArrowIcon />}
                            label="Click to resume your incomplete survey"
                            color="info"
                            size="small"
                            sx={{ mt: 1 }}
                          />
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            );
          })}
        </Stack>
      )}

      {/* Create Survey Dialog */}
      <CreateSurveyDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSave={handleCreateSurvey}
        organization={organization}
      />

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Surveys;
